/**
 * 5897 — Carteira em venda: eventos dos próximos 120 dias com ≥20 pagos,
 * projeção de público vendido com faixa de 80%, confiança e alertas.
 */
import { FC, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PainelPrevisoes } from '../../../types/previsoes'
import { BD, Button, Field, FiltersRow, Input, PageSubtitle, PageTitle, Select, Table } from '../styles'
import {
  BarraReferencia,
  CartaoTabela,
  ComPainel,
  Entenda,
  Kpi,
  Kpis,
  Rolagem,
  SeloAlerta,
  SeloConfianca,
  brl,
  comRef,
  fmtInt,
  useReferencia,
} from './comum'

interface Filtros {
  horizonte: number
  uf: string
  busca: string
  soAlerta: boolean
}
const INICIAL: Filtros = { horizonte: 120, uf: '', busca: '', soAlerta: false }

const Conteudo: FC<{ painel: PainelPrevisoes; referencias: string[] }> = ({ painel, referencias }) => {
  const navigate = useNavigate()
  const [ref] = useReferencia()
  const [f, setF] = useState<Filtros>(INICIAL)
  const K = painel.kpi
  const ufs = useMemo(
    () => Array.from(new Set(painel.eventos.map((e) => e.cidade.split('/')[1]).filter(Boolean))).sort(),
    [painel.eventos],
  )
  const linhas = painel.eventos.filter(
    (e) =>
      e.dias <= f.horizonte &&
      (!f.uf || e.cidade.endsWith(`/${f.uf}`)) &&
      (!f.soAlerta || e.alertas.length > 0) &&
      (!f.busca || e.nome.toLowerCase().includes(f.busca.trim().toLowerCase())),
  )
  const abrir = (id: number): void => navigate(comRef(`/interno/previsoes/evento/${id}`, ref))

  return (
    <>
      <PageTitle>Carteira em venda</PageTitle>
      <PageSubtitle>
        Eventos à venda nos próximos 120 dias com pelo menos 20 ingressos pagos. Projeção de público vendido, faixa
        provável e alertas.
      </PageSubtitle>
      <BarraReferencia painel={painel} referencias={referencias} />
      <Kpis>
        <Kpi rotulo="Eventos em venda" valor={fmtInt(K.eventos)} dica="≥20 pagos · próximos 120 dias" />
        <Kpi rotulo="Pagos até agora" valor={fmtInt(K.pagos)} dica={`${brl(K.receita)} de valor de face`} />
        <Kpi
          rotulo="Projeção (até 30 dias)"
          valor={fmtInt(K.proj30)}
          dica={`faixa ${fmtInt(K.proj30_lo)} – ${fmtInt(K.proj30_hi)}`}
        />
        <Kpi rotulo="Eventos com alerta" valor={fmtInt(K.alertas)} dica="expectativa ou ritmo mudou" />
        <Kpi rotulo="Lotes acabando" valor={fmtInt(K.lotes_acabando)} dica="≥60% de chance em 7 dias" />
      </Kpis>

      <FiltersRow as="form" onSubmit={(e) => e.preventDefault()} style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <Field>
          <label htmlFor="pv-hz">Horizonte</label>
          <Select id="pv-hz" value={f.horizonte} onChange={(e) => setF({ ...f, horizonte: Number(e.target.value) })}>
            <option value={120}>Próximos 120 dias</option>
            <option value={30}>Próximos 30 dias</option>
            <option value={7}>Próximos 7 dias</option>
          </Select>
        </Field>
        <Field>
          <label htmlFor="pv-uf">UF</label>
          <Select id="pv-uf" value={f.uf} onChange={(e) => setF({ ...f, uf: e.target.value })}>
            <option value="">Todas</option>
            {ufs.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </Select>
        </Field>
        <Field>
          <label htmlFor="pv-busca">Buscar evento</label>
          <Input
            id="pv-busca"
            type="search"
            placeholder="nome do evento"
            value={f.busca}
            onChange={(e) => setF({ ...f, busca: e.target.value })}
          />
        </Field>
        <Field>
          <label htmlFor="pv-al">Mostrar</label>
          <Select id="pv-al" value={f.soAlerta ? '1' : '0'} onChange={(e) => setF({ ...f, soAlerta: e.target.value === '1' })}>
            <option value="0">Todos os eventos</option>
            <option value="1">Só com alerta</option>
          </Select>
        </Field>
        <Field style={{ justifyContent: 'flex-end' }}>
          <Button type="button" $variant="secondary" onClick={() => setF(INICIAL)}>
            Limpar filtros
          </Button>
        </Field>
      </FiltersRow>

      <CartaoTabela>
        <Rolagem>
          <Table>
            <thead>
              <tr>
                <th>Evento</th>
                <th className="n">Data</th>
                <th className="n">Pagos</th>
                <th className="n">Ritmo 7 dias</th>
                <th className="n">Projeção · faixa 80%</th>
                <th>Confiança</th>
                <th>Alertas</th>
              </tr>
            </thead>
            <tbody>
              {linhas.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: BD.grayText, padding: 28 }}>
                    Nenhum evento encontrado com esses filtros.
                  </td>
                </tr>
              )}
              {linhas.map((e) => {
                const delta = e.r7a ? (e.r7 - e.r7a) / e.r7a : 0
                return (
                  <tr
                    key={e.id}
                    tabIndex={0}
                    onClick={() => abrir(e.id)}
                    onKeyDown={(ev) => ev.key === 'Enter' && abrir(e.id)}
                  >
                    <td style={{ fontWeight: 500, maxWidth: 340 }}>
                      {e.nome}
                      <small>
                        {e.cidade} · {e.estilo}
                      </small>
                    </td>
                    <td className="n">
                      {e.data}
                      <span className="faixa">D-{e.dias}</span>
                    </td>
                    <td className="n">{fmtInt(e.pagos)}</td>
                    <td className="n">
                      {fmtInt(e.r7)}
                      {e.r7a ? (
                        <span className="faixa" style={{ color: delta < 0 ? '#B42318' : '#067647' }}>
                          {delta >= 0 ? '▲' : '▼'} {Math.abs(Math.round(delta * 100))}% vs sem. ant.
                        </span>
                      ) : (
                        <span className="faixa">sem semana anterior</span>
                      )}
                    </td>
                    <td className="n">
                      <strong>{fmtInt(e.p50)}</strong>
                      <span className="faixa">
                        {fmtInt(e.p10)} – {fmtInt(e.p90)}
                      </span>
                    </td>
                    <td>
                      <SeloConfianca evento={e} />
                    </td>
                    <td>
                      {e.alertas.length > 0 ? (
                        e.alertas.map((a) => <SeloAlerta key={a} alerta={a} />)
                      ) : (
                        <span style={{ color: BD.grayText }}>—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </Rolagem>
      </CartaoTabela>
      <Entenda
        paragrafos={[
          'A projeção é de público VENDIDO (ingressos pagos), não de demanda. A faixa é onde o resultado caiu em ~80% dos eventos no teste com dados de jul/2025 a out/2026.',
          'Até 30 dias do evento o erro típico é 10–25%; de 31 a 120 dias, 28–34% (confiança baixa).',
          'Os alertas são descritivos: "expectativa caiu >30%" acertou 75–89% das vezes no histórico. Eles não dizem o porquê.',
        ]}
      />
    </>
  )
}

export const PrevisoesCarteira: FC = () => (
  <ComPainel>{(painel, referencias) => <Conteudo painel={painel} referencias={referencias} />}</ComPainel>
)
